import React, { useCallback, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import BSButton from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Alert from 'react-bootstrap/Alert';

import Button from 'components/DesignSystem/Button';
import formatLocalDate from 'helper/formatLocalDate';
import {
  WindxTela,
  DownloadZipRequest,
  DownloadZipResponseData,
} from 'helper/windx';
import {
  useGetPainelOptionsQuery,
  useGetModelosDisponiveisQuery,
  useTriggerDownloadZipMutation,
} from 'services/api/windx';
import {
  selectWindxIdsModelos,
  selectWindxOpcoesRmv,
  selectWindxTipoRodada,
  selectWindxDataSimulacao,
} from 'redux/reducers/windxControlPanelReducer';
import ModalDownloadsPronto, {
  DownloadFalha,
  DownloadPronto,
} from './ModalDownloadsPronto';

function todayYmd(): string {
  return formatLocalDate(new Date());
}

// RN-014 (windx-definicao/docs/REQUISITOS.md): o ETL semanal gera o ZIP
// do PMO no dia D com `data_rodada=D-1` por convencao do setor eletrico.
// Logo, o download SM em Estudo Automatico usa D-1 quando o
// modelos-disponiveis nao traz a data do modelo.
function yesterdayYmd(): string {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return formatLocalDate(d);
}

// Motivo do botao desabilitado enquanto chegam as datas do Tipo de Rodada (SM).
const MOTIVO_AGUARDANDO_DATAS_SM = 'Aguardando as datas do tipo de rodada';

/**
 * Dispara o download de um arquivo via <a download> click programatico.
 *
 * Usado APENAS quando N == 1 (download single). Para N > 1 abrimos o
 * `ModalDownloadsPronto` porque browsers (Chrome em especial em maquinas
 * gerenciadas por Enterprise Policy) silenciosamente bloqueiam o 2o+
 * disparo programatico — o unico padrao deterministico cross-browser e
 * exigir um clique de mouse real do usuario por arquivo.
 *
 * Investigacao do bug em 2026-05-15 (ADR-022 windx-definicao + fix
 * backend MR !84): o fix de Content-Disposition no PAR foi NECESSARIO
 * mas nao SUFICIENTE para multi-download — Chrome considera N cliques
 * programaticos dentro do mesmo user gesture como "automatic downloads"
 * e bloqueia.
 */
function triggerBrowserDownload(url: string, filename: string): void {
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.rel = 'noopener noreferrer';
  a.style.display = 'none';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

interface BotaoDownloadProps {
  tela: Extract<WindxTela, 'sh' | 'sm'>;
  iconOnly?: boolean;
}

/**
 * Botao de download dos ZIPs do bucket OCI windx (substituiu o CSV em
 * 2026-05-14, MR backend !82, refinado em !84 com Content-Disposition).
 *
 * UX:
 * - Habilita assim que ha pelo menos 1 modelo selecionado (o ZIP ja contem
 *   todas as entidades/subdivisoes — nao precisa filtro de subdivisao/entidade).
 *   No SM espera tambem as datas do Tipo de Rodada escolhido (modelos-disponiveis):
 *   fica desabilitado enquanto elas carregam — na carga e a cada troca de tipo —,
 *   para nao baixar a rodada do tipo anterior.
 * - **N = 1 modelo selecionado:** dispara download automatico via
 *   `<a download>` (1 user gesture = 1 download, sempre permitido).
 * - **N >= 2 modelos selecionados:** abre `ModalDownloadsPronto` com lista
 *   de PARs prontos. Cada clique em "Baixar" no modal e um user gesture
 *   real, contornando o bloqueio de "automatic downloads" do Chrome
 *   (problema documentado em ADR-022 windx-definicao).
 * - data_rodada = dataSimulacao (Simulacao Antiga) ?? a do modelo no
 *   modelos-disponiveis (no SM, a ultima rodada do Tipo de Rodada escolhido,
 *   WEOL-SM #99) ?? D-1 no SM (RN-014) / hoje no SH.
 * - SH e SM levam o `tipo_rodada`; no SM ele vira o sufixo `-{tipo}` do zip.
 *
 * Backend route: POST /produtos/windx/download/zip/ — retorna { url, filename,
 * file_size_bytes, expires_in_hours } onde `url` e PAR OCI (72h) com query
 * parameter `httpResponseContentDisposition` ja anexado pelo fix do MR !84.
 */
export default function BotaoDownload({ tela, iconOnly = false }: BotaoDownloadProps) {
  const [erro, setErro] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [prontos, setProntos] = useState<DownloadPronto[]>([]);
  const [falhas, setFalhas] = useState<DownloadFalha[]>([]);
  const [triggerDownload, { isLoading }] = useTriggerDownloadZipMutation();

  const idsModelos = useSelector(selectWindxIdsModelos);
  const opcoesRmv = useSelector(selectWindxOpcoesRmv);
  const tipoRodada = useSelector(selectWindxTipoRodada);
  const dataSimulacao = useSelector(selectWindxDataSimulacao);

  // Reutiliza o cache do RTK Query: os params espelham os do
  // useControlPanelRules (mesmo request do painel). Fornece o `data_rodada`
  // efetivo por modelo — necessario para SM Estudo Automatico, onde o ETL pode
  // ter rodado antes de ontem. WEOL-SM (#99): com o tipo, no SM a data de cada
  // modelo e a da ultima rodada DAQUELE tipo.
  // WINDX-034: dataSimulacao eh a unica fonte da verdade. Sem ela, modo
  // Automatico (request omite data_simulacao). Sempre dispara.
  // WINDX-030: com uma variante de RMV marcada, filtra por ela; com as duas,
  // omite o filtro (BE devolve a uniao).
  let comRmv: boolean | undefined;
  if (opcoesRmv.length === 1) {
    comRmv = opcoesRmv[0] === 'com_rmv';
  }
  const modelosDispParams: Parameters<typeof useGetModelosDisponiveisQuery>[0] = {
    tela,
    ...(dataSimulacao ? { data_simulacao: dataSimulacao } : {}),
    ...(tipoRodada ? { tipo_rodada: tipoRodada } : {}),
    ...(comRmv !== undefined ? { com_rmv: comRmv } : {}),
  };
  const {
    data: modelosDisp,
    isFetching: buscandoModelosDisp,
  } = useGetModelosDisponiveisQuery(modelosDispParams);

  // WEOL-SM (#99): no SM a data do zip depende do tipo. Na troca do tipo, o RTK
  // Query devolve em `data` as datas do tipo anterior ate a resposta nova chegar;
  // o download espera por ela para nao baixar a rodada errada. No SH a data e a
  // mesma para qualquer tipo (data_referencia), entao nada muda.
  // Basta o `isFetching`: no RTK Query 1.9 o `useQuery` ja o devolve `true` (no
  // lugar de `isUninitialized`) no render entre a troca dos params e o inicio do
  // request.
  const aguardandoDatasSM = tela === 'sm' && buscandoModelosDisp;
  // Na espera, o botao diz por que esta desabilitado: o `title` vira a dica do
  // mouse e a descricao para o leitor de tela.
  const motivoDesabilitado = aguardandoDatasSM ? MOTIVO_AGUARDANDO_DATAS_SM : undefined;

  // Painel options ja eh carregado pelo ControlPanel (RTK Query da cache
  // global). Aqui reusa o cache para mapear id -> codigo canonico do modelo
  // (ex: id=2 -> codigo='WRF'). O codigo bate com o nome do diretorio
  // {MODELO} no bucket OCI.
  const { data: painelOptions } = useGetPainelOptionsQuery(tela);

  const codigosSelecionados = useMemo<string[]>(() => {
    if (!painelOptions || idsModelos.length === 0) return [];
    return painelOptions.modelos
      .filter((m) => idsModelos.includes(m.id))
      .map((m) => m.codigo);
  }, [painelOptions, idsModelos]);

  const temModeloSelecionado = codigosSelecionados.length > 0;

  const handleDownload = useCallback(async () => {
    if (!temModeloSelecionado || aguardandoDatasSM) return;
    setErro(null);

    // Um "job" = um request ao backend. Em SH espelhamos o painel: se o
    // usuario marcou com_rmv E sem_rmv, geramos 1 job por (modelo x variante).
    // Em SM ha sempre 1 variante (com_rmv) — 1 job por modelo.
    interface DownloadJob {
      codigo: string;
      rmv?: boolean;
    }

    const buildJobs = (): DownloadJob[] => {
      if (tela === 'sm') {
        return codigosSelecionados.map((codigo) => ({ codigo }));
      }
      // SH: estado defensivo — se nenhuma variante marcada, assume com_rmv.
      // Cast mantem o tipo OpcaoRmv[] sem precisar importar o type name.
      const variantes = opcoesRmv.length > 0 ? opcoesRmv : (['com_rmv'] as typeof opcoesRmv);
      return codigosSelecionados.flatMap((codigo) => variantes.map((opcao) => ({
        codigo,
        rmv: opcao === 'com_rmv',
      })));
    };

    const buildPayload = (job: DownloadJob): DownloadZipRequest => {
      // SM Estudo Automatico: usa data_rodada efetivo do backend; fallback D-1.
      // Simulacao Antiga: usa dataSimulacao. SH: dataSimulacao ou hoje.
      const dataRodadaModelo = modelosDisp?.modelos
        .find((m) => m.codigo === job.codigo)?.data_rodada;
      const dataRodada = dataSimulacao
        ?? dataRodadaModelo
        ?? (tela === 'sm' ? yesterdayYmd() : todayYmd());
      return {
        tipo: tela,
        data_rodada: dataRodada,
        modelo: job.codigo,
        // SH-only: o zip SM e sempre com_rmv.
        ...(tela === 'sh' ? { rmv: job.rmv ?? true } : {}),
        // WINDX-056: tipoRodada pode ser undefined no boot (sem default). Na
        // pratica os modelos so aparecem depois do Tipo de Rodada (com a flag do
        // BE desligada, o painel do SM assume 'final'); o fallback 'final' e
        // defensivo, satisfaz o contrato e espelha o default do BE.
        // WEOL-SM (#99, download-zip v2.7): vale tambem no SM, onde vira o
        // sufixo `-{tipo}` do zip.
        tipo_rodada: tipoRodada ?? 'final',
      };
    };

    const jobs = buildJobs();

    // Promise.allSettled para nao abortar os ZIPs que existem caso um deles
    // retorne 404 (ex.: sem_rmv inexistente para a rodada).
    const results = await Promise.allSettled(
      jobs.map((job) => triggerDownload(buildPayload(job)).unwrap()),
    );

    const sucessosTuple: { codigo: string; data: DownloadZipResponseData }[] = [];
    const falhasLista: DownloadFalha[] = [];

    results.forEach((result, idx) => {
      const { codigo } = jobs[idx];
      if (result.status === 'fulfilled') {
        sucessosTuple.push({ codigo, data: result.value });
      } else {
        const reason = result.reason as { data?: { description?: string; message?: string } };
        const motivo = reason?.data?.description
          || reason?.data?.message
          || 'erro desconhecido';
        let variante: string | undefined;
        if (jobs[idx].rmv !== undefined) {
          variante = jobs[idx].rmv ? 'com_rmv' : 'sem_rmv';
        }
        falhasLista.push({ modelo: codigo, motivo, variante });
      }
    });

    // Caso UX 1: 1 unico arquivo e nenhuma falha — download automatico.
    if (sucessosTuple.length === 1 && falhasLista.length === 0) {
      const { url, filename } = sucessosTuple[0].data;
      triggerBrowserDownload(url, filename);
      return;
    }

    // Caso UX 2: nenhum sucesso — so alerta de erro.
    if (sucessosTuple.length === 0) {
      const lista = falhasLista.map((f) => `${f.modelo}: ${f.motivo}`).join('; ');
      setErro(`Nenhum ZIP disponivel para a rodada selecionada. ${lista}`);
      return;
    }

    // Caso UX 3: multi-download (varios modelos e/ou variantes) — modal.
    // O filename (com com_rmv/sem_rmv) distingue as variantes do mesmo modelo.
    setProntos(sucessosTuple.map(({ codigo, data }) => ({
      modelo: codigo,
      url: data.url,
      filename: data.filename,
      fileSizeBytes: data.file_size_bytes,
    })));
    setFalhas(falhasLista);
    setShowModal(true);
  }, [
    temModeloSelecionado,
    aguardandoDatasSM,
    tela,
    codigosSelecionados,
    opcoesRmv,
    tipoRodada,
    dataSimulacao,
    modelosDisp,
    triggerDownload,
  ]);

  const handleDismissErro = useCallback(() => setErro(null), []);
  const handleHideModal = useCallback(() => setShowModal(false), []);

  const labelLoading = codigosSelecionados.length > 1
    ? `Preparando ${codigosSelecionados.length} ZIPs…`
    : 'Preparando ZIP…';

  return (
    <div>
      {iconOnly ? (
        <Button
          icon="download"
          small
          type="tertiary"
          onClick={handleDownload}
          disabled={!temModeloSelecionado || isLoading || aguardandoDatasSM}
          // Icon-only: o nome vem do `title`. Na espera o title vira o motivo, e o
          // aria-label mantem o nome "Download ZIP"; fora dela, nada muda.
          title={motivoDesabilitado ?? 'Download ZIP'}
          aria-label={motivoDesabilitado ? 'Download ZIP' : undefined}
          id="windx-download-button"
        />
      ) : (
        <BSButton
          variant="outline-secondary"
          size="sm"
          onClick={handleDownload}
          disabled={!temModeloSelecionado || isLoading || aguardandoDatasSM}
          title={motivoDesabilitado}
          data-testid="botao-download-zip"
        >
          {isLoading ? (
            <>
              <Spinner animation="border" size="sm" role="status" className="me-1" />
              {labelLoading}
            </>
          ) : 'Download ZIP'}
        </BSButton>
      )}

      {erro && (
        <Alert
          variant="danger"
          className="mt-2 py-1 px-2 small"
          onClose={handleDismissErro}
          dismissible
          data-testid="botao-download-erro"
        >
          {erro}
        </Alert>
      )}

      <ModalDownloadsPronto
        show={showModal}
        onHide={handleHideModal}
        prontos={prontos}
        falhas={falhas}
      />
    </div>
  );
}
